import { CommonModule, formatNumber } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { SystemAdminService } from "../../../services/system-admin.service";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../../utils/no-whitespace.validator';
import { ToastService } from "../../../services/toast.service";
import { ToastType } from "../../../enums/config.enum";


@Component({
    selector: 'app-quote-parameters',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, ReactiveFormsModule],
    templateUrl: './quote-parameters.component.html',
    styleUrl: './quote-parameters.component.scss'
})
export class QuoteParametersComponent implements OnInit {
    systemAdminService = inject(SystemAdminService);
    isQuoteConfigUpdate = signal<boolean>(false);
    toastService = inject(ToastService);
    isReadonly = signal<boolean>(true);
    configId = signal<number>(0);
    fb: FormBuilder = inject(FormBuilder);
    quoteConfigForm: any = this.fb.group({
        indicationExpiryDays: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        indicationExpiryNotificationDays: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        indicationPrefix: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
        indicationStartNo: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
        currentIndicationNo: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],

        quoteExpiryDays: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        quoteExpiryNotificationDays: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        quotePrefix: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(64)]],
        quoteStartNo: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
        currentQuoteNo: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],

        policyStaticPrefix: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]{3}$')]],
        policySequenceNumber: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        currentPolicyNumber: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$')]],
        policyStaticCharacter: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]{1}$')]],
        policySuffix: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],

        versionMaxCount: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        maxAOPCreditAllowed: [null, [Validators.required, noWhitespaceValidator]],
        // maxAOPCredit: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^-?\d+(\.\d+)?$')]],
        minAOPRate: [null, [Validators.required, noWhitespaceValidator]],
        maxCATCreditAllowed: [null, [Validators.required, noWhitespaceValidator]],
        minCATRate: [null, [Validators.required, noWhitespaceValidator]],
        tivLimit: [null, [Validators.required, noWhitespaceValidator]],

    });

    ngOnInit(): void {
        this.getQuoteConfigDetails();
    }

    // getQuoteConfigDetails() {
    //     this.systemAdminService.getQuoteConfig().subscribe({
    //         next: (response: any) => {
    //             if(response){
    //                 this.isQuoteConfigUpdate.set(true);
    //             }
    //         },
    //         error: (error: any) => {
    //             console.log("error", error);
    //         }
    //     })
    // }

    formatCurrencyValue(value: any): string {
      if (value == null || value === '') {
        return '';
      }
      return formatNumber(Number(value), 'en-US', '1.0-2');
    }

    cleanCurrencyValue(value: string | number): number {
        if (typeof value === "number") {
          return value; // Already a number, return as is
          
        }
        // Remove all non-numeric characters except the decimal point
        const cleanedValue = value.replace(/[^0-9.-]+/g, '');
        
        return parseFloat(cleanedValue) || 0; // Convert to number, default to 0 if invalid
      }
      
      
    formatCurrency(event: any): void {
        let value = event.target.value;
        value = value.replace(/[^0-9.-]+/g, ''); // Remove non-numeric characters
        
        if (value && !isNaN(value)) {
          let num = Number(value);
          // Format the number using 'en-US' locale (comma separators)
          let formattedValue = new Intl.NumberFormat('en-US', {
           
            minimumFractionDigits: 0, // You can adjust this if you need decimal points
            maximumFractionDigits:2
      
          }).format(num);
      
          // Update the input field with the formatted value
          // this.businessPropertyLimit = num;
          
          event.target.value = formattedValue;
      
          // this.calculateTotals();
        }
    }


    getQuoteConfigDetails() {
        this.systemAdminService.getQuoteConfig().subscribe({
            next: (response: any) => {
                if (response && response.length > 0) {
                    response.forEach((quoteConfig: any) => {
                        // Process the single object in the response
                        this.quoteConfigForm.patchValue({
                            indicationExpiryDays: quoteConfig.indicationExpiryDays,
                            indicationExpiryNotificationDays: quoteConfig.indicationExpiryNotificationDays,
                            indicationPrefix: quoteConfig.indicationPrefix,
                            indicationStartNo: quoteConfig.indicationStartNo,
                            currentIndicationNo: quoteConfig.currentIndicationNo,
                            quoteExpiryDays: quoteConfig.quoteExpiryDays,
                            quoteExpiryNotificationDays: quoteConfig.quoteExpiryNotificationDays,
                            quotePrefix: quoteConfig.quotePrefix,
                            quoteStartNo: quoteConfig.quoteStartNo,
                            currentQuoteNo: quoteConfig.currentQuoteNo,
                            policyStaticPrefix: quoteConfig.policyStaticPrefix,
                            policySequenceNumber: quoteConfig.policySequenceNumber,
                            currentPolicyNumber: quoteConfig.currentPolicyNumber,
                            policyStaticCharacter: quoteConfig.policyStaticCharacter,
                            policySuffix: quoteConfig.policySuffix,
                            versionMaxCount: quoteConfig.versionMaxCount,
                            maxAOPCreditAllowed: quoteConfig.maxAOPCreditAllowed,
                            minAOPRate: quoteConfig.minAOPRate,
                            maxCATCreditAllowed: quoteConfig.maxCATCreditAllowed,
                            minCATRate: quoteConfig.minCATRate,
                            tivLimit: this.formatCurrencyValue(quoteConfig.tivLimit),

                        });
                        this.configId.set(quoteConfig.configId);
                        this.isQuoteConfigUpdate.set(true);
                    });
                } else {
                    console.error('Response is invalid or empty:', response);
                }
            },
            error: (error: any) => {
                console.error('Error fetching mail config details:', error);
            }
        });
    }

    saveQuoteConfig(payload: any) {
        this.systemAdminService.saveQuoteConfig(payload).subscribe({
            next: (response: any) => {
                if (response) {
                    this.toastService.addNotification({ message: "Quote config created", type: ToastType.Success });

                    // if response is success show the prompt
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    updateQuoteConfig(configId: number, payload: any) {
        this.systemAdminService.updateQuoteConfig(configId, payload).subscribe({
            next: (response: any) => {
                if (response) {
                    this.isReadonly.set(true);
                    this.toastService.addNotification({ message: "Quote config successfully updated", type: ToastType.Success });

                    // if response is success show the prompt
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    onQuoteConfigSubmit() {
        if (this.quoteConfigForm.invalid) {
            this.quoteConfigForm.markAllAsTouched();
            return;
        }

        const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const userId = userDetails.userId;

        const payload = this.quoteConfigForm.getRawValue() as any;
        payload.tivLimit = this.cleanCurrencyValue(payload.tivLimit)
        payload.createdUserId = userId;
        payload.updatedUserId = userId;
        payload.createdDate = new Date().toISOString();
        payload.updatedDate = new Date().toISOString();

        this.isQuoteConfigUpdate() ? this.updateQuoteConfig(this.configId(), payload) : this.saveQuoteConfig(payload);
    }

    toggleReadOnly(){
        this.isReadonly.set(!this.isReadonly());
    }

    cancelClick(){
        this.getQuoteConfigDetails();
        this.isReadonly.set(true);
    }
}


